import base64 from 'base-64';
import zpipe from 'zpipe';
import * as THREE from 'three';
import { API_PATH } from '../../config';

const parsePointCloud = (rawData) => {
  const expanded = zpipe.inflate(base64.decode(rawData));
  const rawPoints = new Uint8Array(expanded.length);

  for (let i = 0; i < expanded.length; i += 1) {
    rawPoints[i] = expanded.charCodeAt(i);
  }

  const dataView = new DataView(rawPoints.buffer);

  const pointCloud = [...Array(dataView.byteLength / 8).keys()].map((idx) => {
    const offset = idx * 8;
    return new THREE.Vector3(
        dataView.getFloat32(offset, true),
        0,
        dataView.getFloat32(offset + 4, true),
    );
  });

  return pointCloud;
};

const removeMesh = (child) => {
  if (child.type === 'Mesh') {
    if (child.geometry) child.geometry.dispose();
    if (child.material) child.material.dispose();
    child.remove();
  }
};

/**
* PointCloudLoader
*/
export default class PointCloudLoader {
  /**
  * Constructor
  */
  constructor() {
    /* Create */
    this.ballCloudPoints = [];
    this.onCompleted = this.onCompleted.bind(this);

    /* Debug pointcloud */
    this.pointCloudGeometry = new THREE.BufferGeometry();
    const emptyPositions = new Float32Array(999999);
    const emptyAlphas = new Float32Array(999999);

    this.pointCloudGeometry.addAttribute(
        'position',
        new THREE.BufferAttribute(emptyPositions, 3),
    );

    this.pointCloudGeometry.addAttribute(
        'alpha',
        new THREE.BufferAttribute(emptyAlphas, 1),
    );

    // eslint-disable-next-line max-len
    this.pointCloudPositions = this.pointCloudGeometry.attributes.position.array;
    this.pointCloudMaterial = new THREE.PointsMaterial({ color: 0x8888aa });

    this.cloud = new THREE.Points(
        this.pointCloudGeometry,
        this.pointCloudMaterial,
    );

    this.cloud.name = 'thePointcloud';

    /* End debug pointcloud */

    /* Debug ballcloud */
    this.ballCloud = undefined;
    this.ballGeometry = new THREE.BoxGeometry(0.1, 0.1, 0.1);
    this.ballMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    /* End debug ballcloud */
  }

  /**
  * Load the panorama point cloud data
  *
  * @param {string} panoramaId
  */
  start(panoramaId) {
    fetch(`${API_PATH}/pointcloud`, {
      method: 'POST',
      body: JSON.stringify({
        panoId: panoramaId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    })
        .then((result) => result && result.json && result.json())
        .then((result) => {
          this.ballCloudPoints = result.pointCloud
            ? parsePointCloud(result.pointCloud)
            : [];

          this.onCompleted();
        });
  }

  /**
   * Override
   */
  onCompleted() {
    console.error('onCompleted should be overriden', this.ballCloudPoints);
  }

  /**
   * Clear.
   */
  clear() {
    this.parsedPoints = null;

    if (this.ballCloud) {
      this.ballCloud.traverse(removeMesh);
      this.ballCloud = null;
    }
  }


  /**
  * Point Cloud for debug
  */
  createPointCloud() {
    /* Real pointcloud,
    * which cannot be intersected with our old school radar box
    */
    this.pointCloudGeometry.attributes.position.needsUpdate = true;
    this.pointCloudGeometry.attributes.alpha.needsUpdate = true;
    this.pointCloudGeometry.setDrawRange(0, this.ballCloudPoints.length * 3);

    // Create point cloud "mesh"
    const pointCloudMaterial = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      visible: false,
    });

    this.pointCloudObj = new THREE.Points(
        this.pointCloudGeometry,
        pointCloudMaterial,
    );

    for (let i = 0, l = this.ballCloudPoints.length; i < l; i += 3) {
      this.pointCloudPositions[i] = this.ballCloudPoints[i].x;
      this.pointCloudPositions[i + 1] = this.ballCloudPoints[i].y;
      this.pointCloudPositions[i + 2] = this.ballCloudPoints[i].z;
    }
  }

  /**
  * Ball Cloud for debug
  */
  createBallCloud() {
    if (!this.ballCloud) {
      this.ballCloud = new THREE.Group();
      this.ballCloud.name = 'ballCloud';
    }

    for (let i = 0; i < this.ballCloudPoints.length; i += 1) {
      // Could we use one combined mesh?
      const ball = new THREE.Mesh(this.ballGeometry, this.ballMaterial);
      const pos = this.ballCloudPoints[i];
      ball.numericID = i;
      ball.position.copy(pos);
      this.ballCloud.add(ball);
    }
  }
}
